import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useUserContext } from "../hooks/UserContext";
import { useNotification } from "../hooks/NotificationContext";
import { Producto } from "../models/ProductoModel";
import { LineaVenta } from "../models/LineaVentaModel";
import { fetchGetProductosParaVenta } from "../services/productoService";
import { 
  Button, 
  Dialog, 
  Grid, 
  Box, 
  Typography, 
  Alert,
  AlertTitle,
  TextField,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Chip,
  Divider,
  Collapse,
  CircularProgress
} from "@mui/material";
import DynamicGrid from "../components/DynamicTable";
import {
  fetchGetMesaAbierta,
  fetchGetMesaAbiertaPorNombre,
  fetchGuardarComandaMesa,
  mapMesaDetalleToLineaVenta,
  MesaAbiertaResponse
} from "../services/mesaService";
import RestaurantIcon from '@mui/icons-material/Restaurant';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteIcon from "@mui/icons-material/Delete";
import TableBarIcon from '@mui/icons-material/TableBar';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { CurrencyFormatter } from "../utils/CurrencyFormatter";
import LocalSelector from "./components/LocalSelector";
import { useAppConfig } from "../hooks/AppConfigContext";
import { parseDecimalValue } from "../utils/NumberParser";
import { isAbortError } from "../services/fetchService";


export const gridColumnDefinitions = {
  productos: {
    'PRODUCTO': { Header: 'Producto', Editable: false },
    'CODIGO': { Header: 'Codigo', Editable: false },
    'CONTADO': { Header: '$ Unitario', Editable: false, IsNumber: true },
  },
};

const ComandaMesa = () => {
  const { getUser, user } = useUserContext();
  const { showNotification } = useNotification();
  const { productoVentaHeaders } = useAppConfig();
  const [idLocal, setIdLocal] = useState<string | undefined>(undefined);
  const [productos, setProductos] = useState<Producto[]>([]);
  const [selectDialogOpen, setSelectDialogOpen] = useState<boolean>(false);
  const [numeroMesa, setNumeroMesa] = useState<string>("");
  const [nombreComanda, setNombreComanda] = useState<string>("");
  const [lineaVenta, setLineaVenta] = useState<LineaVenta[]>([]);
  const showMessage = useCallback((message: string, severity: "success" | "info" | "warning" | "error") => {
    showNotification(message, severity);
  }, [showNotification]);
  const [loadingProductos, setLoadingProductos] = useState(false);
  const [checkingMesa, setCheckingMesa] = useState(false);
  const [mesaAbiertaDetalle, setMesaAbiertaDetalle] = useState<MesaAbiertaResponse | undefined>(undefined);
  const [mesaAbiertaCargada, setMesaAbiertaCargada] = useState(false);
  const [idsComandasOriginales, setIdsComandasOriginales] = useState<number[]>([]);
  const [expandedNotes, setExpandedNotes] = useState<{ [key: number]: boolean }>({});
  const [editingNotes, setEditingNotes] = useState<{ [key: number]: string }>({});
  const mesaBusquedaActualRef = useRef({ idLocal: 0, identificador: "" });
  const pv = 1;
  const productosGridHeaders = {
    PRODUCTO: productoVentaHeaders.PRODUCTO,
    CODIGO: productoVentaHeaders.CODIGO,
    CONTADO: productoVentaHeaders.CONTADO
  };


  const blurActiveElement = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  const closeProductDialog = () => {
    blurActiveElement();
    setSelectDialogOpen(false);
  };

  const handleOpenProductDialog = () => {
    if (!idLocal) {
      showMessage("Debe seleccionar un local primero", 'error');
      return;
    }

    if (!numeroMesa.trim() && !nombreComanda.trim()) {
      showMessage("Debe ingresar un número de mesa o un nombre", 'error');
      return;
    }

    if (productos.length === 0) {
      showMessage("Cargando productos...", 'info');
      return;
    }

    blurActiveElement();
    setSelectDialogOpen(true);
  };

  const userLoggedIn = useMemo(() => user ?? getUser(), [getUser, user]);

  const normalizeProducto = (producto: any): Producto => ({
    IDPRODUCTO: Number(producto.IDPRODUCTO ?? producto.idProducto ?? producto.IdProducto ?? 0),
    CODIGO: String(producto.CODIGO ?? producto.codigo ?? producto.Codigo ?? ""),
    PRODUCTO: String(producto.PRODUCTO ?? producto.producto ?? producto.Producto ?? ""),
    CONTADO: parseDecimalValue(producto.CONTADO ?? producto.contado ?? producto.Contado ?? producto.precio ?? producto.Precio ?? 0)
  });

  const getUnitario = (linea: LineaVenta): number => {
    const producto = linea.producto as any;
    return parseDecimalValue(producto.CONTADO ?? producto.contado ?? producto.Contado ?? producto.precio ?? producto.Precio ?? 0);
  };

  useEffect(() => {
    if (userLoggedIn && idLocal) {
      const controller = new AbortController();
      setLoadingProductos(true);
      fetchGetProductosParaVenta(idLocal, userLoggedIn!, controller.signal)
        .then((prods: Producto[]) => {
          setProductos(prods);
        })
        .catch(err => {
          if (isAbortError(err)) return;
          console.log(err);
          showMessage("Error al cargar productos", 'error');
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoadingProductos(false);
        });

      return () => controller.abort();
    }
  }, [idLocal, showMessage, userLoggedIn]);

  useEffect(() => {
    mesaBusquedaActualRef.current = {
      idLocal: Number(idLocal ?? 0),
      identificador: numeroMesa.trim() || nombreComanda.trim()
    };

    if (!userLoggedIn || !idLocal || (!numeroMesa.trim() && !nombreComanda.trim())) {
      setMesaAbiertaDetalle(undefined);
      setMesaAbiertaCargada(false);
      setIdsComandasOriginales([]);
      return;
    }

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => {
      setCheckingMesa(true);
      const idLocalBuscado = Number(idLocal);
      const numeroMesaBuscado = numeroMesa.trim();
      const nombreBuscado = nombreComanda.trim();
      const consulta = numeroMesaBuscado
        ? fetchGetMesaAbierta(userLoggedIn, idLocalBuscado, pv, numeroMesaBuscado, controller.signal)
        : fetchGetMesaAbiertaPorNombre(userLoggedIn, idLocalBuscado, pv, nombreBuscado, controller.signal);
      consulta
        .then((mesa: MesaAbiertaResponse) => {
          if (
            mesaBusquedaActualRef.current.idLocal !== idLocalBuscado ||
            mesaBusquedaActualRef.current.identificador !== (numeroMesaBuscado || nombreBuscado)
          ) {
            return;
          }

          const detalleMesa = mesa.detalle ?? [];
          const mesaTieneDetalle = detalleMesa.length > 0;
          const mesaAbierta = Boolean(mesa.abierta || mesaTieneDetalle);
          setMesaAbiertaDetalle(mesaAbierta ? {
            abierta: true,
            comanda: mesa.comanda,
            detalle: detalleMesa
          } : undefined);
          setMesaAbiertaCargada(false);
          setIdsComandasOriginales([]);
        })
        .catch(err => {
          if (isAbortError(err)) return;
          console.log(err);
          setMesaAbiertaDetalle(undefined);
          setMesaAbiertaCargada(false);
          setIdsComandasOriginales([]);
        })
        .finally(() => {
          if (!controller.signal.aborted) setCheckingMesa(false);
        });
    }, 600);

    return () => {
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [userLoggedIn, idLocal, numeroMesa, nombreComanda]);

  const handleProductClick = (params: any) => {
    try {
      const row = normalizeProducto(params.row);
      
      // Always add as a new item to allow separate notes for each instance
      // Each line can have its own quantity and notes
      setLineaVenta([...lineaVenta, { producto: row, cantidad: 1, notas: "" }]);
      
      closeProductDialog();
    } catch (error) {
      console.error("Error adding product:", error);
    }
  };

  const getLineasMesaAbierta = (): LineaVenta[] => {
    return mesaAbiertaDetalle?.detalle?.map(mapMesaDetalleToLineaVenta) ?? [];
  };

  const calcularTotalLineas = (lineas: LineaVenta[]): number => {
    return lineas.reduce(
      (total, linea) => total + (getUnitario(linea) * linea.cantidad),
      0
    );
  };

  const getLineasMesaAbiertaNoCargadas = (): LineaVenta[] => {
    return mesaAbiertaCargada ? [] : getLineasMesaAbierta();
  };

  const traerDetalleMesaAbierta = () => {
    const lineasMesa = getLineasMesaAbierta();

    if (lineasMesa.length === 0) {
      setLineaVenta([]);
      setMesaAbiertaCargada(true);
      setIdsComandasOriginales([]);
      showMessage("Mesa abierta sin detalle. Puede agregar productos.", 'info');
      return;
    }

    setLineaVenta(lineasMesa);
    setMesaAbiertaCargada(true);
    setIdsComandasOriginales(Array.from(new Set(lineasMesa.map((linea) => linea.idComanda).filter(Boolean) as number[])));
    showMessage("Detalle de mesa cargado", 'success');
  };

  const handleIncrementQuantity = (rowIndex: number) => {
    const updatedLineaVenta = [...lineaVenta];
    updatedLineaVenta[rowIndex].cantidad += 1;
    setLineaVenta(updatedLineaVenta);
  };

  const handleDecrementQuantity = (rowIndex: number) => {
    const updatedLineaVenta = [...lineaVenta];
    if (updatedLineaVenta[rowIndex].cantidad > 1) {
      updatedLineaVenta[rowIndex].cantidad -= 1;
      setLineaVenta(updatedLineaVenta);
    }
  };

  const handleDeleteRow = (rowIndex: number) => {
    const updatedLineaVenta = [...lineaVenta];
    updatedLineaVenta.splice(rowIndex, 1);
    setLineaVenta(updatedLineaVenta);
    // Clean up expanded/editing state for this row
    const newExpandedNotes = { ...expandedNotes };
    const newEditingNotes = { ...editingNotes };
    delete newExpandedNotes[rowIndex];
    delete newEditingNotes[rowIndex];
    setExpandedNotes(newExpandedNotes);
    setEditingNotes(newEditingNotes);
  };

  const toggleNotesExpand = (rowIndex: number) => {
    setExpandedNotes(prev => ({
      ...prev,
      [rowIndex]: !prev[rowIndex]
    }));
    // Initialize editing state if needed
    if (!editingNotes[rowIndex]) {
      setEditingNotes(prev => ({
        ...prev,
        [rowIndex]: lineaVenta[rowIndex].notas || ""
      }));
    }
  };

  const handleNotesChange = (rowIndex: number, value: string) => {
    setEditingNotes(prev => ({
      ...prev,
      [rowIndex]: value
    }));
  };

  const saveNotes = (rowIndex: number) => {
    const updatedLineaVenta = [...lineaVenta];
    updatedLineaVenta[rowIndex].notas = editingNotes[rowIndex];
    setLineaVenta(updatedLineaVenta);
    setExpandedNotes(prev => ({
      ...prev,
      [rowIndex]: false
    }));
  };

  const calcularTotal = (): number => {
    return calcularTotalLineas(lineaVenta);
  };

  const calcularTotalMesa = (): number => {
    return calcularTotalLineas(lineaVenta) + calcularTotalLineas(getLineasMesaAbiertaNoCargadas());
  };

  const guardarComanda = () => {
    if (!userLoggedIn || !idLocal || (!numeroMesa.trim() && !nombreComanda.trim())) {
      showMessage("Complete todos los campos requeridos", 'error');
      return;
    }

    if (lineaVenta.length === 0) {
      showMessage("No hay productos en la comanda", 'error');
      return;
    }

    fetchGuardarComandaMesa(userLoggedIn!, Number(idLocal), numeroMesa.trim(), lineaVenta, {
      pv,
      eliminarAnterior: true,
      idComandasOriginales: idsComandasOriginales,
      nombre: nombreComanda.trim()
    })
      .then((res: any) => {
        const success = Boolean(res?.success ?? true);
        showMessage(success ? "Comanda enviada correctamente" : res?.message ?? "No se pudo guardar la comanda", success ? 'success' : 'error');
        
        if (success) {
          // Reset form
          setLineaVenta([]);
          setNumeroMesa("");
          setNombreComanda("");
          setMesaAbiertaDetalle(undefined);
          setMesaAbiertaCargada(false);
          setIdsComandasOriginales([]);
        }
      })
      .catch(err => {
        showMessage("Error al guardar la comanda", 'error');
        console.log(err);
      });
  };

  const handleLocalChange = (idLocal: string | undefined) => {
    setIdLocal(idLocal);
    setMesaAbiertaDetalle(undefined);
    setMesaAbiertaCargada(false);
    setIdsComandasOriginales([]);
    setLineaVenta([]);
  };

  const handleNumeroMesaChange = (value: string) => {
    setNumeroMesa(value);
    setMesaAbiertaDetalle(undefined);
    setMesaAbiertaCargada(false);
    setIdsComandasOriginales([]);
    setLineaVenta([]);
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Typography 
        variant="h4" 
        gutterBottom 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }
        }}
      >
        <RestaurantIcon fontSize="large" color="primary" />
        Comanda de Mesa
      </Typography>

      {/* Local and Table Selection */}
      <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mb: { xs: 2, sm: 3 } }}>
        <Grid item xs={12} md={4}>
          <LocalSelector onLocalChange={handleLocalChange} />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Número de mesa (opcional)"
            variant="outlined"
            fullWidth
            value={numeroMesa}
            onChange={(e) => handleNumeroMesaChange(e.target.value)}
            helperText={checkingMesa ? "Buscando mesa abierta..." : " "}
            InputProps={{
              startAdornment: <TableBarIcon sx={{ mr: 1, color: 'action.active' }} />,
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Nombre (opcional)"
            variant="outlined"
            fullWidth
            value={nombreComanda}
            inputProps={{ maxLength: 120 }}
            onChange={(e) => setNombreComanda(e.target.value)}
            helperText="Ej.: Juan, Mostrador o Pedido 15"
          />
        </Grid>
      </Grid>

      {mesaAbiertaDetalle && !mesaAbiertaCargada && (
        <Alert
          severity="warning"
          sx={{ mb: { xs: 2, sm: 3 } }}
          action={
            <Button color="inherit" size="small" onClick={traerDetalleMesaAbierta}>
              Traer detalle
            </Button>
          }
        >
          <AlertTitle>Mesa abierta</AlertTitle>
          La comanda {numeroMesa ? `de la mesa ${numeroMesa}` : nombreComanda} ya está abierta. Puede traer el detalle existente para continuar.
        </Alert>
      )}

      {mesaAbiertaCargada && (
        <Alert severity="success" sx={{ mb: { xs: 2, sm: 3 } }}>
          Detalle existente de {numeroMesa ? `la mesa ${numeroMesa}` : nombreComanda} cargado. Puede agregar más productos.
        </Alert>
      )}

      {mesaAbiertaDetalle && (
        <Box sx={{ mb: { xs: 2, sm: 3 }, p: { xs: 1.5, sm: 2 }, border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Grid container alignItems="center" justifyContent="space-between" spacing={1}>
            <Grid item xs={12} sm="auto">
              <Typography variant="subtitle1" fontWeight="bold">
                Total {numeroMesa ? `mesa ${numeroMesa}` : nombreComanda}
              </Typography>
            </Grid>
            <Grid item xs={12} sm="auto">
              <Typography variant="h6" fontWeight="bold" color="primary" sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                {CurrencyFormatter.format(calcularTotalMesa())}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Add Product Button */}
      <Box sx={{ mb: { xs: 2, sm: 3 } }}>
        <Button
          onClick={handleOpenProductDialog}
          variant="contained"
          color="primary"
          fullWidth
          disabled={loadingProductos || checkingMesa || !idLocal || (!numeroMesa.trim() && !nombreComanda.trim())}
          sx={{ 
            textTransform: "none", 
            fontWeight: "bold", 
            py: { xs: 1.5, sm: 2 },
            fontSize: { xs: '0.9rem', sm: '1rem' }
          }}
          startIcon={loadingProductos ? <CircularProgress color="inherit" size={18} /> : <AddIcon />}
        >
          {loadingProductos ? "Cargando Menu..." : "Agregar Productos"}
        </Button>
      </Box>

      {/* Product Selection Dialog */}
      <Dialog 
        open={selectDialogOpen} 
        fullWidth 
        maxWidth="md"
        fullScreen={window.innerWidth < 600}
        onClose={closeProductDialog}
      >
        <Box sx={{ p: { xs: 1, sm: 2 } }}>
          <Typography variant="h5" gutterBottom sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
            Seleccionar Producto
          </Typography>
          <DynamicGrid
            data={productos}
            customHeaderMap={productosGridHeaders}
            pageSize={10}
            removeIfNotCustom={true}
            rowClickEventHandler={handleProductClick}
          />
        </Box>
      </Dialog>

      {/* Order Summary */}
      <Box sx={{ mb: { xs: 2, sm: 3 } }}>
        <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
          {numeroMesa ? `Mesa: ${numeroMesa}` : `Nombre: ${nombreComanda || "Sin asignar"}`}
        </Typography>
        <Divider sx={{ mb: { xs: 1, sm: 2 } }} />

        {lineaVenta.length === 0 ? (
          <Alert severity="info" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            No hay productos en la comanda. Agregue productos para comenzar.
          </Alert>
        ) : (
          <Grid container spacing={{ xs: 1, sm: 2 }}>
            {lineaVenta.map((linea, index) => (
              <Grid item xs={12} key={index}>
                <Card elevation={2}>
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                    <Grid container spacing={{ xs: 1, sm: 2 }} alignItems="center">
                      {/* Product Name and Code - Full width on mobile */}
                      <Grid item xs={12} sm={5}>
                        <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                          {linea.producto.PRODUCTO}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          Codigo: {linea.producto.CODIGO}
                        </Typography>
                        {linea.notas && !expandedNotes[index] && (
                          <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 0.5, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                            {linea.notas}
                          </Typography>
                        )}
                      </Grid>
                      
                      {/* Quantity Controls - Left aligned on mobile */}
                      <Grid item xs={6} sm={3}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, justifyContent: { xs: 'flex-start', sm: 'center' } }}>
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleDecrementQuantity(index)}
                            sx={{ p: { xs: 0.5, sm: 1 } }}
                          >
                            <RemoveIcon fontSize="small" />
                          </IconButton>
                          <Chip 
                            label={`${linea.cantidad}x`} 
                            color="primary" 
                            size={window.innerWidth < 600 ? "small" : "medium"}
                            sx={{ minWidth: { xs: 50, sm: 60 }, fontWeight: 'bold' }}
                          />
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleIncrementQuantity(index)}
                            sx={{ p: { xs: 0.5, sm: 1 } }}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Grid>
                      
                      {/* Price - Right aligned */}
                      <Grid item xs={6} sm={3}>
                        <Typography 
                          variant="h6" 
                          align="right"
                          sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                        >
                          {CurrencyFormatter.format(getUnitario(linea) * linea.cantidad)}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          align="right"
                          sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}
                        >
                          {CurrencyFormatter.format(getUnitario(linea))} c/u
                        </Typography>
                      </Grid>
                      
                      {/* Action Buttons - Horizontal on mobile, vertical on desktop */}
                      <Grid item xs={12} sm={1}>
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: { xs: 'row', sm: 'column' }, 
                          gap: 0.5,
                          justifyContent: { xs: 'flex-end', sm: 'center' }
                        }}>
                          <IconButton 
                            size="small"
                            color="primary"
                            onClick={() => toggleNotesExpand(index)}
                            title={linea.notas ? "Editar notas" : "Agregar notas"}
                            sx={{ p: { xs: 0.5, sm: 1 } }}
                          >
                            {linea.notas ? <EditNoteIcon fontSize="small" /> : <NoteAddIcon fontSize="small" />}
                          </IconButton>
                          <IconButton 
                            size="small"
                            color="error"
                            onClick={() => handleDeleteRow(index)}
                            sx={{ p: { xs: 0.5, sm: 1 } }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Grid>
                    </Grid>
                    
                    {/* Notes Section - Collapsible */}
                    <Collapse in={expandedNotes[index]} timeout="auto" unmountOnExit>
                      <Box sx={{ mt: { xs: 1, sm: 2 }, pt: { xs: 1, sm: 2 }, borderTop: 1, borderColor: 'divider' }}>
                        <TextField
                          label="Notas / Comentarios"
                          placeholder="Ej: Sin cebolla, bien cocido, para llevar..."
                          multiline
                          rows={2}
                          fullWidth
                          value={editingNotes[index] || ""}
                          onChange={(e) => handleNotesChange(index, e.target.value)}
                          variant="outlined"
                          size={window.innerWidth < 600 ? "small" : "medium"}
                        />
                        <Box sx={{ 
                          mt: 1, 
                          display: 'flex', 
                          justifyContent: 'flex-end', 
                          gap: { xs: 0.5, sm: 1 },
                          flexWrap: 'wrap'
                        }}>
                          <Button
                            size="small"
                            onClick={() => toggleNotesExpand(index)}
                            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                          >
                            Cancelar
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => saveNotes(index)}
                            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                          >
                            Guardar
                          </Button>
                        </Box>
                      </Box>
                    </Collapse>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Total and Submit */}
      {lineaVenta.length > 0 && (
        <Card 
          elevation={3} 
          sx={{ 
            bgcolor: 'primary.main', 
            color: 'primary.contrastText',
            position: { xs: 'sticky', sm: 'static' },
            bottom: { xs: 0, sm: 'auto' },
            zIndex: { xs: 1000, sm: 'auto' }
          }}
        >
          <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
            <Grid container alignItems="center" justifyContent="space-between" spacing={{ xs: 1, sm: 0 }}>
              <Grid item xs={12} sm="auto">
                <Typography 
                  variant="h5"
                  sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                >
                  Total
                </Typography>
              </Grid>
              <Grid item xs={12} sm="auto">
                <Typography 
                  variant="h4" 
                  fontWeight="bold"
                  sx={{ 
                    fontSize: { xs: '1.75rem', sm: '2.125rem' },
                    textAlign: { xs: 'left', sm: 'right' }
                  }}
                >
                  {CurrencyFormatter.format(calcularTotal())}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
          <CardActions sx={{ 
            justifyContent: { xs: 'stretch', sm: 'flex-end' }, 
            p: { xs: 1.5, sm: 2 },
            pt: { xs: 0, sm: 2 }
          }}>
            <Button
              onClick={guardarComanda}
              variant="contained"
              color="success"
              size="large"
              fullWidth={window.innerWidth < 600}
              sx={{ 
                textTransform: "none", 
                fontWeight: "bold", 
                px: { xs: 2, sm: 4 },
                py: { xs: 1.5, sm: 1 },
                fontSize: { xs: '1rem', sm: '1.125rem' }
              }}
            >
              Enviar Comanda
            </Button>
          </CardActions>
        </Card>
      )}

    </Box>
  );
};

export default ComandaMesa;



